import { type Components, type Theme } from '@mui/material/styles';
import type {} from '@mui/material/themeCssVarsAugmentation';

import { radius, touchTarget } from './tokens';

/** The four severities that get a standard-variant tint. */
type AlertTone = 'success' | 'info' | 'warning' | 'error';

/**
 * One standard Alert's surface, derived from its own severity colour.
 *
 * MUI builds a standard Alert's background by lightening `main` by 90%, which
 * assumes `main` is a saturated colour on a white page. Neither holds here:
 * the tokens are dark, legible inks (`warning` is #8A5B0F), and alerts sit on
 * `surfaceVariant` as often as on paper. Lightening #8A5B0F that far lands on
 * #F3EFE8, which is one percent away from the #F2EFEF band behind it — the
 * notice was invisible on exactly the page that most needs it read.
 *
 * Composing from `mainChannel` instead keeps the tint tied to the token, so it
 * cannot drift when a token moves and it inverts for free in dark mode. The
 * border does the real work: on a tinted band a fill alone is always marginal,
 * whereas an edge separates the box at any backdrop.
 */
const alertTone =
  (tone: AlertTone) =>
  ({ theme }: { theme: Theme }) => ({
    backgroundColor: `rgba(var(--mui-palette-${tone}-mainChannel) / 0.12)`,
    borderColor: `rgba(var(--mui-palette-${tone}-mainChannel) / 0.38)`,
    // The root is set to body ink so a five-line disclaimer reads as text
    // rather than as a block of coloured warning; the icon carries severity.
    '& .MuiAlert-icon': { color: theme.vars.palette[tone].main, opacity: 1 },
  });

export const components: Components<Theme> = {
  // MUI renders subtitle1/subtitle2 as <h6> by default, which puts a heading
  // in the outline for what is really a lede paragraph (§50).
  MuiTypography: {
    defaultProps: {
      variantMapping: { subtitle1: 'p', subtitle2: 'p' },
    },
  },

  MuiCssBaseline: {
    styleOverrides: (theme) => ({
      'html, body': { minHeight: '100%' },
      body: { WebkitFontSmoothing: 'antialiased' },
      ':focus-visible': {
        outline: `2px solid ${theme.vars.palette.primary.main}`,
        outlineOffset: 2,
      },
      // §50 / §52 — honour reduced motion everywhere, including MUI transitions.
      '@media (prefers-reduced-motion: reduce)': {
        '*, *::before, *::after': {
          animationDuration: '0.01ms !important',
          animationIterationCount: '1 !important',
          transitionDuration: '0.01ms !important',
          scrollBehavior: 'auto !important',
        },
      },
      // Tabular figures wherever digits line up: prices, mileage, spec tables.
      '.tabular-nums': { fontVariantNumeric: 'tabular-nums' },
    }),
  },

  // The spacing unit is 4px (MUI's default is 8), so components that pad with
  // theme.spacing() are restored to their standard 16/24px here. Check this list
  // when adopting Accordion, Tabs or Table.
  MuiContainer: {
    styleOverrides: {
      root: ({ theme }) => ({
        paddingLeft: 16,
        paddingRight: 16,
        [theme.breakpoints.up('sm')]: { paddingLeft: 24, paddingRight: 24 },
      }),
    },
  },
  MuiToolbar: {
    styleOverrides: {
      gutters: ({ theme }) => ({
        paddingLeft: 16,
        paddingRight: 16,
        [theme.breakpoints.up('sm')]: { paddingLeft: 24, paddingRight: 24 },
      }),
    },
  },
  MuiBreadcrumbs: {
    styleOverrides: { separator: { marginLeft: 8, marginRight: 8 } },
  },
  MuiMenuItem: {
    styleOverrides: { gutters: { paddingLeft: 16, paddingRight: 16 } },
  },

  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: { borderRadius: radius.sm, minHeight: touchTarget, paddingInline: 20 },
      sizeSmall: { minHeight: 36, paddingInline: 14 },
      sizeLarge: { minHeight: 52, paddingInline: 28, fontSize: '1rem' },
      outlined: { borderWidth: 1.5, '&:hover': { borderWidth: 1.5 } },
    },
  },

  MuiIconButton: {
    styleOverrides: { root: { minWidth: touchTarget, minHeight: touchTarget } },
  },

  MuiCard: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: radius.md,
        border: `1px solid ${theme.vars.palette.divider}`,
      }),
    },
  },

  MuiPaper: {
    styleOverrides: { rounded: { borderRadius: radius.md } },
  },

  MuiDialog: {
    styleOverrides: { paper: { borderRadius: radius.lg } },
  },

  MuiOutlinedInput: {
    styleOverrides: { root: { borderRadius: radius.sm } },
  },

  // Labels sit above fields rather than floating: clearer on mobile and for
  // screen readers (§39, §50).
  MuiTextField: {
    defaultProps: { variant: 'outlined', fullWidth: true },
  },

  MuiChip: {
    styleOverrides: { root: { borderRadius: radius.sm, fontWeight: 600 } },
  },

  MuiLink: {
    defaultProps: { underline: 'hover' },
  },

  MuiAlert: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: radius.md,
        border: '1px solid',
        borderColor: 'transparent',
        color: theme.vars.palette.text.primary,
      }),
      standardSuccess: alertTone('success'),
      standardInfo: alertTone('info'),
      standardWarning: alertTone('warning'),
      standardError: alertTone('error'),
    },
  },
};
